import { z } from "zod";

import { paginatedSchema, paginationQuerySchema } from "../common/pagination";

/**
 * A gateway that stayed disconnected past its alert window (ADR-093).
 *
 * Super admin only. Unlike an incident it has no tenant and no device: the
 * gateway may be shared infrastructure, and its audience is whoever runs the
 * platform's radio network.
 */
export const gatewayOutageSchema = z.object({
  id: z.uuid(),
  gatewayId: z.uuid(),
  gatewayName: z.string(),
  gatewayEui: z.string(),
  /** Null for shared platform infrastructure. */
  organizationName: z.string().nullable(),
  siteName: z.string().nullable(),

  /** First poll that found it disconnected. */
  startedAt: z.iso.datetime(),
  /** When super admins were emailed. */
  openedAt: z.iso.datetime(),
  /** Set by the next poll that finds it connected, never by a person. */
  resolvedAt: z.iso.datetime().nullable(),
  /** The gateway's last heartbeat or relayed uplink. */
  lastSeenAt: z.iso.datetime().nullable(),

  acknowledgedAt: z.iso.datetime().nullable(),
  acknowledgedByName: z.string().nullable(),
  acknowledgeNote: z.string().nullable(),

  /** Devices configured against this gateway, across every tenant. */
  deviceCount: z.number().int().nonnegative(),
});

export type GatewayOutage = z.infer<typeof gatewayOutageSchema>;

export const listGatewayOutagesQuerySchema = paginationQuerySchema.extend({
  /** Unresolved by default; `open=false` returns the history as well. */
  open: z.stringbool().default(true),
  gatewayId: z.uuid().optional(),
});

export type ListGatewayOutagesQuery = z.infer<typeof listGatewayOutagesQuerySchema>;

export const acknowledgeGatewayOutageRequestSchema = z.object({
  note: z.string().trim().min(1).max(500).optional(),
});

export type AcknowledgeGatewayOutageRequest = z.infer<typeof acknowledgeGatewayOutageRequestSchema>;

export const gatewayOutageListSchema = paginatedSchema(gatewayOutageSchema);
export type GatewayOutageList = z.infer<typeof gatewayOutageListSchema>;
